import { ConsoleSqlOutlined } from '@ant-design/icons';
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { HttpRequest } from '../utils/httpClient';
import config from '../config';
import { createLogger } from '../utils/logger';
const logger = createLogger('contexts');

// Define the interface for the assistant
export interface Assistant {
  id: string;
  accountId: string;
  name: string;
  createdAt: number;
  externalId: string;
  merchantId: string;
  status: number;
  /**
   * Kind of agent. `rta` (Real-Time Assistant) renders its own overview modules
   * — see `modulesFor` in AgentOverviewPage/agent-modules.ts. The backend already
   * returns this on the chatbot payload; it just was not typed here.
   */
  agentType?: 'rta' | 'conversational' | 'process' | 'qa';
  /**
   * Per-agent kill switch for the Process Flows module (default ON). When the
   * backend returns `false`, the "Flujos de proceso" card and menu item are
   * hidden for this agent. Absent/true → shown. No UI toggle; set in the DB.
   */
  processFlowsEnabled?: boolean;
  businessName?: string;
  communicationStyle?: string;
  responseLength?: string;
  voiceLanguage?: string;
  languages?: string[];
  voiceConfig?: any;
  whatsappConfig?: {
    phoneNumberId?: string;
    accessToken?: string;
    verifyToken?: string;
    [key: string]: any;
  };
  openAIConfig?: {
    max_tokens?: number | null;
  };
  domainTopic?: string;
  specialist?: string;
  fallbackResponse?: string;
  /** Topics the agent can talk about. Stored as one comma-separated string. */
  chatTopics?: string;
  /** LLM provider + model. Source of truth for the model; `openAIConfig` is legacy. */
  modelSettings?: {
    provider?: string;
    model?: string;
  };
  acronyms?: any[];
  /** Structured tags the agent uses to label conversations (AGE-278). */
  tagTopics?: Array<{ id: string; title: string; description?: string }>;
  /** Persisted business rules (top-level field returned by GET, saved via PATCH /config). */
  businessRules?: string[];
  advancedConfig?: {
    guardrails: any[];
    businessRules: any[];
  };
  guardrailsConfig?: {
    inputGuards?: string[];
    outputGuards?: string[];
  };
  inactivityThreshold?: number;
  /** Optimistic-lock counter; sent on privacy-config PATCH. */
  version?: number;
  /** Persistent-memory retention in days (7–180). */
  memoryTtlDays?: number;
  /** Per-field PII redaction config for persistent memory. */
  piiRedactionConfig?: {
    enabled: boolean;
    fields: Array<'email' | 'phone' | 'card' | 'address' | 'documentId'>;
  };
}

// Define the interface for the context
interface AssistantContextType {
  currentAssistant: Assistant | null;
  availableAssistants: Assistant[];
  setAssistant: (assistantId: string) => void;
  isValidAssistant: (assistantId: string) => boolean;
  loadAssistants: (accountId: string, forceRefresh?: boolean) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

// Create the context
const AssistantContext = createContext<AssistantContextType | undefined>(undefined);

// Context provider
export const AssistantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentAssistant, setCurrentAssistant] = useState<Assistant | null>(null);
  const [availableAssistants, setAvailableAssistants] = useState<Assistant[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { accountId, AssistantID } = useParams();
  const lastAssistantIdRef = useRef<string | null>(null);
  const lastAccountIdRef = useRef<string | null>(null);

  // Load available assistants for the current account
  const loadAssistants = useCallback(async (accountId: string, forceRefresh = false) => {
    if (!accountId || (accountId === lastAccountIdRef.current && !forceRefresh)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    // if the account id has changed, reset the assistant id and the current assistant
    if (accountId !== lastAccountIdRef.current) {
      lastAssistantIdRef.current = null;
      setCurrentAssistant(null);
    }

    lastAccountIdRef.current = accountId;

    try {
      const request = new HttpRequest(`${config.endpoints.assistants}/account/${accountId}`);
      const response = await request.get();

      if (response?.data?.chatbots && Array.isArray(response.data.chatbots)) {
        const assistants = response.data.chatbots;
        setAvailableAssistants(assistants);

        // If there's an assistant selected in the URL, verify if it's valid
        if (AssistantID && AssistantID !== lastAssistantIdRef.current) {
          const assistant = assistants.find(asst => asst.id === AssistantID);
          if (assistant) {
            const infoRequest = new HttpRequest(`${config.endpoints.chatbot}/info`);
            const infoResponse = await infoRequest.get(AssistantID);
            if (infoResponse?.data?.data) {
              const assistantInfo = infoResponse.data.data;
              setCurrentAssistant(assistantInfo);
              lastAssistantIdRef.current = AssistantID;
            } else {
              logger.error(
                `Failed to load assistant info for ${AssistantID} (status: ${infoResponse?.status}):`,
                infoResponse?.data
              );
            }
          } else {
            // The AssistantID in the URL is not valid for this account.
            // Do NOT fall back to a default assistant — leave the selection empty.
            setCurrentAssistant(null);
            lastAssistantIdRef.current = null;
          }
        }
        // No AssistantID in the URL: do not auto-select any assistant.
        // The assistant context (and its sidebar section) only appears when
        // the user explicitly selects an assistant.
      } else {
        setAvailableAssistants([]);
      }
    } catch (error) {
      logger.error('Error al obtener asistentes:', error);
      setError('Error al cargar los asistentes. Por favor, inténtalo de nuevo.');
      setAvailableAssistants([]);
    } finally {
      setIsLoading(false);
    }
  }, [AssistantID]);

  // Load assistants when the account changes
  useEffect(() => {
    if (accountId) {
      loadAssistants(accountId);
    }
  }, [accountId, loadAssistants]);

  // Function to set the current assistant
  const setAssistant = async (assistantId: string) => {
    if (assistantId === lastAssistantIdRef.current) {
      return;
    }

    const assistant = availableAssistants.find(asst => asst.id === assistantId);
    if (assistant) {
      const infoRequest = new HttpRequest(`${config.endpoints.chatbot}/info`);
      const infoResponse = await infoRequest.get(assistantId);
      if (infoResponse?.data?.data) {
        const assistantInfo = infoResponse.data.data;
        setCurrentAssistant(assistantInfo);
        lastAssistantIdRef.current = assistantId;
      } else {
        logger.error(
          `Failed to load assistant info for ${assistantId} (status: ${infoResponse?.status}):`,
          infoResponse?.data
        );
      }
    }
  };

  // Function to verify if an assistant is valid
  const isValidAssistant = (assistantId: string) => {
    return availableAssistants.some(asst => asst.id === assistantId);
  };

  return (
    <AssistantContext.Provider
      value={{
        currentAssistant,
        availableAssistants,
        setAssistant,
        isValidAssistant,
        loadAssistants,
        isLoading,
        error
      }}
    >
      {children}
    </AssistantContext.Provider>
  );
};

// Custom hook to use the context
export const useAssistant = () => {
  const context = useContext(AssistantContext);
  if (context === undefined) {
    throw new Error('useAssistant must be used within an AssistantProvider');
  }
  return context;
};

/**
 * Same context without the throw, for components that render both inside and
 * outside the provider — the channels module is mounted by the agent's Home
 * (inside) and by the setup wizard, and is unit-tested standalone. Mirrors
 * `useAgentDraftOptional`.
 */
export const useAssistantOptional = (): AssistantContextType | null =>
  useContext(AssistantContext) ?? null; 
